import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, PageHead, StatusDot } from "@/components/kit";
import { Factory, User, Zap, CalendarClock } from "lucide-react";

const STAGES = ["scripting", "visuals", "voiceover", "bgm", "assembly"];

export default function Production() {
    const qc = useQueryClient();
    const [channelId, setChannelId] = useState("");
    const [title, setTitle] = useState("");
    const [format, setFormat] = useState("short");
    const [renderMode, setRenderMode] = useState("cinematic");
    const [calItem, setCalItem] = useState("");

    const { data: channels = [] } = useQuery({
        queryKey: ["channels"],
        queryFn: async () => (await api.get("/channels")).data,
    });
    const { data: calendar = [] } = useQuery({
        queryKey: ["calendar"],
        queryFn: async () => (await api.get("/calendar")).data,
    });
    const scheduled = calendar.filter((c) => c.status === "scheduled");
    const { data: jobs = [] } = useQuery({
        queryKey: ["jobs"],
        queryFn: async () => (await api.get("/jobs")).data,
        refetchInterval: 2500,
    });
    // Only in-flight work belongs in the Production queue: rendering + waiting for
    // approval. Approved/published/rejected jobs leave so it never contradicts what
    // was actually uploaded (see Publicación / Command Center).
    const activeJobs = jobs.filter((j) =>
        ["producing", "ready_for_approval"].includes(j.status));

    const autoProduce = useMutation({
        mutationFn: async () => (await api.post("/production/auto")).data,
        onSuccess: (d) => {
            if (d.queued > 0) {
                toast.success(`Auto-producción: ${d.queued} video(s) del calendario en marcha`);
            } else {
                toast(d.message || "Nada agendado para hoy", { icon: "ℹ" });
            }
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["calendar"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo en auto-producción"),
    });

    const produceItem = useMutation({
        mutationFn: async (id) => (await api.post(`/calendar/${id}/produce`)).data,
        onSuccess: () => {
            toast.success("Producción lanzada desde el calendario");
            setCalItem("");
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["calendar"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo lanzando producción"),
    });

    const create = useMutation({
        mutationFn: async () =>
            (await api.post("/jobs", { channel_id: channelId, title, format, render_mode: renderMode })).data,
        onSuccess: () => {
            toast.success("Job lanzado · Minimax escribiendo guion");
            setTitle("");
            qc.invalidateQueries({ queryKey: ["jobs"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo lanzando job"),
    });

    return (
        <div data-testid="production-page">
            <PageHead
                title="PRODUCCIÓN · PIXELLE-VIDEO"
                sub="Minimax escribe → visuales por API → Edge-TTS narra → BGM → ensamblaje FFmpeg."
            />

            <Panel
                title="Producción automática · desde el motor de estrategia"
                testId="auto-production-panel"
                className="mb-6 border-primary/60"
            >
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <select
                        data-testid="calendar-item-select"
                        value={calItem}
                        onChange={(e) => setCalItem(e.target.value)}
                        className="flex-1 border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                    >
                        <option value="">
                            {scheduled.length
                                ? `Títulos agendados en estrategia (${scheduled.length})…`
                                : "Sin títulos agendados — planea en Estrategia"}
                        </option>
                        {scheduled.map((it) => (
                            <option key={it.id} value={it.id}>
                                {it.date} · {it.title} · {it.format.toUpperCase()} · {it.channel_name}
                            </option>
                        ))}
                    </select>
                    <Btn
                        testId="produce-selected-btn"
                        variant="ghost"
                        onClick={() => calItem && produceItem.mutate(calItem)}
                        disabled={produceItem.isPending || !calItem}
                    >
                        <span className="flex items-center gap-2">
                            <CalendarClock size={13} />
                            {produceItem.isPending ? "Lanzando…" : "Producir seleccionado"}
                        </span>
                    </Btn>
                    <Btn
                        testId="auto-produce-btn"
                        onClick={() => autoProduce.mutate()}
                        disabled={autoProduce.isPending}
                    >
                        <span className="flex items-center gap-2">
                            <Zap size={13} />
                            {autoProduce.isPending ? "Encolando…" : "Auto-producir lo de hoy"}
                        </span>
                    </Btn>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                    Auto-producir lanza en cadena los videos agendados para hoy (o atrasados) con el título y
                    formato ya decididos por darkzOGx — sin elegir nada. Aparecerán abajo conforme se generen.
                </p>
            </Panel>

            <Panel title="Lanzar producción manual" testId="production-form" className="mb-6">
                <div className="flex flex-col gap-3 md:flex-row">
                    <select
                        data-testid="job-channel-select"
                        value={channelId}
                        onChange={(e) => setChannelId(e.target.value)}
                        className="border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary md:w-56"
                    >
                        <option value="">Canal…</option>
                        {channels.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                    <input
                        data-testid="job-title-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Título del video"
                        className="flex-1 border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                    />
                    <select
                        data-testid="job-format-select"
                        value={format}
                        onChange={(e) => setFormat(e.target.value)}
                        className="border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary md:w-40"
                    >
                        <option value="short">Short 9:16</option>
                        <option value="long">Largo 16:9</option>
                    </select>
                    <select
                        data-testid="job-render-mode-select"
                        value={renderMode}
                        onChange={(e) => setRenderMode(e.target.value)}
                        className="border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary md:w-64"
                    >
                        <option value="cinematic">Cinematográfico · clips reales Hailuo / Veo / Sora</option>
                        <option value="economic">Económico · imágenes animadas (menor costo)</option>
                    </select>
                    <Btn
                        testId="job-create-btn"
                        onClick={() => channelId && title.trim() && create.mutate()}
                        disabled={create.isPending || !channelId || !title.trim()}
                    >
                        <span className="flex items-center gap-2">
                            <Factory size={14} /> Producir
                        </span>
                    </Btn>
                </div>
            </Panel>

            <div className="grid grid-cols-1 gap-px bg-border lg:grid-cols-2">
                {activeJobs.map((j) => {
                    const producing = j.status === "producing";
                    return (
                        <div key={j.id} className="bg-card p-5" data-testid={`job-card-${j.id}`}>
                            <div className="mb-3 flex items-start gap-4">
                                <div className="relative h-16 w-28 shrink-0 overflow-hidden border border-border">
                                    <img src={j.thumbnail} alt="" className="h-full w-full object-cover opacity-80" />
                                    {producing && <div className="scanline absolute inset-0" />}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-head text-sm leading-tight">{j.title}</h3>
                                        <span className="text-[10px] uppercase text-primary">{j.format}</span>
                                    </div>
                                    <div className="mt-1 text-xs text-muted-foreground">{j.channel_name}</div>
                                    <div className="mt-1 flex items-center gap-2 text-[11px]">
                                        <StatusDot
                                            color={producing ? "#00FFFF" : "#39FF14"}
                                            pulse={producing}
                                        />
                                        {producing ? j.stage_label : "Listo para aprobar"}
                                    </div>
                                </div>
                            </div>

                            {/* pipeline */}
                            <div className="mb-2 flex gap-1">
                                {STAGES.map((st) => {
                                    const done = (j.done_stages || []).includes(st) || !producing;
                                    const cur = producing && j.stage === st;
                                    return (
                                        <div
                                            key={st}
                                            className="h-1.5 flex-1"
                                            style={{
                                                background: done
                                                    ? "#39FF14"
                                                    : cur
                                                    ? "#00FFFF"
                                                    : "hsl(var(--secondary))",
                                            }}
                                        />
                                    );
                                })}
                            </div>
                            <div className="flex justify-between text-[10px] text-muted-foreground">
                                <span>{j.hook}</span>
                                <span className="text-primary">{j.progress}%</span>
                            </div>
                        </div>
                    );
                })}
            </div>
            {!activeJobs.length && (
                <p className="mt-6 text-sm text-muted-foreground">
                    Sin jobs en producción ni pendientes de aprobar. Lanza uno arriba o desde el
                    calendario de estrategia. Los videos ya aprobados/publicados están en Publicación.
                </p>
            )}
        </div>
    );
}
